import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from '@sb-shared/services/local-storage.service';
import { WindowLocationService } from '@sb-shared/services/window-location.service';
import { OAuthService, TokenResponse } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from '../auth.config';
import { Observable, from, map, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  appAccessToken: string;
  tokenRefresh$: Observable<string>;
  tokenPredictedExpiryTimestampMs: number;

  constructor(
    private oauth: OAuthService,
    private router: Router,
    private storage: LocalStorageService,
    private location: WindowLocationService
  ) {}

  init(): Promise<unknown> {
    //this.oauth.events.subscribe(e => console.log(e));

    this.appAccessToken = this.storage.get('appAccessToken', false);

    if (this.appAccessToken) {
      // Injected Access Token clients - bypass standard OAuth.
      window.EveryBuddy.CurrentOrganisationId = this.storage.get('currentOrganisationId');

      return new Promise(resolve => {
        resolve(true);
      });
    }

    this.oauth.configure(authCodeFlowConfig);
    this.oauth.setupAutomaticSilentRefresh();

    // If a redirect to the IDP is about to happen (no token yet), seed the OAuth `state`
    // with the path the user originally requested so we can restore it after the round-trip.
    // The IDP-side `redirect_uri` is just the origin, which would otherwise drop the deep link.
    // Skip when the URL already carries a `code` / `state` (an OAuth callback in progress),
    // otherwise we'd overwrite the real state with the callback URL.
    const search = window.location.search ?? '';
    const isOAuthCallback = /[?&](code|state|error)=/.test(search);
    const initialPath = isOAuthCallback ? '' : `${window.location.pathname}${search}${window.location.hash}`;
    const loginOptions = initialPath && initialPath !== '/' ? { state: initialPath } : undefined;

    return this.oauth
      .loadDiscoveryDocumentAndLogin(loginOptions)
      .then((isLoggedIn: boolean) => {
        if (!isLoggedIn) {
          console.warn('User not logged in');

          return new Promise(resolve => {
            resolve(true);
          });
        }

        return this.oauth.loadUserProfile().then((data: { info: { sb_sod; sb_soid; sb_oids; force_logout } }) => {
          if (data.info.force_logout) {
            console.warn('Forcing logout', data);
            this.logout();
          } else if (
            !!data.info.sb_sod &&
            this.location.origin !== data.info.sb_sod &&
            environment.localOverrideOrgId === undefined
          ) {
            // if the current origin doesn't match the organisation domain, process redirection.
            // Skipped when `environment.localOverrideOrgId` is set (SB-15223: running the SPA locally
            // against Azure test APIs), otherwise the dev would be kicked to the real org domain.
            this.location.redirect(data.info.sb_sod);
          } else {
            // Prefer the organisation id from the OIDC profile. When running with a local override
            // and the profile didn't select one (e.g. user has access to multiple orgs and no
            // sb_selected_org cookie exists), fall back to the configured organisation id.
            const selectedOrganisationId = data.info.sb_soid ?? environment.localOverrideOrgId;

            this.storage.set('currentOrganisationId', selectedOrganisationId);
            this.storage.set(
              'canSwitchOrganisation',
              !!data.info.sb_oids && (data.info.sb_oids.split(',').length > 1 || data.info.sb_oids === '*')
            );
            window.EveryBuddy.CurrentOrganisationId = selectedOrganisationId;

            if (environment.localOverrideOrgId !== undefined && !data.info.sb_soid) {
              console.info(
                `[SB-15223] Running with localOverrideOrgId; no sb_soid in profile, pinned organisationId=${environment.localOverrideOrgId}`
              );
            }

            // see if an initially requested path is included in the OAuth additional state
            const redirect_uri = decodeURIComponent(this.oauth.state ?? '');
            if (redirect_uri && redirect_uri !== '/') {
              // redirect to initially requested page
              console.log('[AuthService] navigating to', redirect_uri);
              this.router.navigateByUrl(redirect_uri);
            }
          }
        });
      })
      .catch(err => {
        console.error('Error on log-in, forcing logout', err);
        this.logout();
      });
  }

  getAccessToken() {
    return this.appAccessToken ?? this.oauth.getAccessToken();
  }

  isLoggedIn() {
    return this.appAccessToken || this.oauth.hasValidAccessToken();
  }

  /**
   * Get access token, including a refresh beforehand if expired. May have expired if for some reason the
   * automatic silent refresh has failed, e.g. browser tab or computer went to sleep.
   * @returns Observable on access token. Null if no valid token in first place.
   */
  getTokenAndRefreshIfExpired(): Observable<string> {
    // Mobile app injects its own access token.
    if (this.appAccessToken) {
      return of(this.appAccessToken);
    }

    if (!this.oauth.getAccessToken()) {
      console.warn('auth.service.getTokenAndRefreshIfExpired(): no token found, so cannot refresh');

      return of(null);
    }

    if (this.tokenRefresh$) {
      // Already a pending refresh
      return this.tokenRefresh$;
    }

    if (this.tokenPredictedExpiryTimestampMs) {
      const tokenRemainingMs = this.tokenPredictedExpiryTimestampMs - new Date().getTime();

      // The oauth library function 'hasValidAccessToken' includes an expiry check, but applies a 10 minute skew.
      // This means tokens will be indicated as valid up to 10 minutes after the expiry time.
      // We'll do our own check instead, without skew.
      if (tokenRemainingMs > 0) {
        // If time since UNIX epoch is before the token expiry
        // Token still valid
        return of(this.oauth.getAccessToken());
      }

      console.warn(`Auth Token expired ${Math.abs(tokenRemainingMs)}ms ago, will attempt refresh`);
    } else {
      console.warn('Auth Token expiry unknown, will attempt refresh');
    }

    // Token has expired
    this.tokenRefresh$ = from(this.oauth.refreshToken()).pipe(
      map(tokenResponse => this.getTokenFromResponse(tokenResponse)),
      tap(() => (this.tokenRefresh$ = null))
    ); // clear observable ready in case it expires again.

    return this.tokenRefresh$;
  }

  refreshToken(): Observable<string> {
    // Mobile app injects its own access token.
    if (this.appAccessToken) {
      return of(this.appAccessToken);
    }

    if (!this.oauth.getAccessToken()) {
      return of(null);
    }

    if (this.tokenRefresh$) {
      // Already a pending refresh
      return this.tokenRefresh$;
    }
    this.tokenRefresh$ = from(this.oauth.refreshToken()).pipe(
      map(tokenResponse => this.getTokenFromResponse(tokenResponse)),
      tap(() => (this.tokenRefresh$ = null))
    ); // clear observable ready in case it expires again.

    return this.tokenRefresh$;
  }

  getTokenFromResponse(tokenResponse: TokenResponse): string {
    // Calculate our own token expiry timestamp using exires_in (seconds) minus a 10 second buffer
    this.tokenPredictedExpiryTimestampMs = new Date().getTime() + tokenResponse.expires_in * 1000 - 10000;

    return tokenResponse.access_token;
  }

  isAppAccessToken() {
    return !!this.appAccessToken;
  }

  logout() {
    if (this.isAppAccessToken()) {
      // Injected Access Token clients - clear local storage value and refresh to redirect to log-in page.
      this.storage.remove('appAccessToken');
      this.location.reload();
      return;
    }

    // Everyone else, standard OAuth logout.
    this.oauth.postLogoutRedirectUri = `${window.EveryBuddy.OrganisingUrl}/Account/BackChannelLogout?orgDomain=${encodeURIComponent(window.EveryBuddy.AngularUrl)}`;
    this.oauth.revokeTokenAndLogout();
  }
}
